import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Pagination,
  CircularProgress,
  Alert,
  Stack,
  TextField,
  MenuItem,
} from '@mui/material';
import apiService from '../../services/api';
import { AdminActivityLog, ProfessionalReplyNotificationEntry } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

const PAGE_SIZE = 10;

type NotificationEntry =
  | (AdminActivityLog & { type: 'admin'; professional_display?: string | null; professional_user_code?: string | null })
  | (ProfessionalReplyNotificationEntry & {
      type: 'reply';
      professional_display?: string | null;
      professional_display_id?: number;
    });

const resolveProfessionalDisplay = (entry: NotificationEntry) => {
  if (entry.professional_display) {
    return entry.professional_display;
  }
  const metadataName = entry.metadata?.professional_name;
  const metadataCode = entry.metadata?.professional_user_code;
  const name = entry.professional_name || metadataName;
  const code = entry.professional_user_code ?? metadataCode;
  if (name) {
    if (code) {
      return `${name} (${code})`;
    }
    if (entry.professional_id) {
      return `${name} (ID: ${entry.professional_id})`;
    }
    return name;
  }
  if (code) {
    return code;
  }
  return null;
};

const NotificationsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const [logs, setLogs] = useState<NotificationEntry[]>([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: PAGE_SIZE,
    total: 0,
  });
  const [messageFilter, setMessageFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [markingRead, setMarkingRead] = useState(false);
  const canViewActivityLogs = hasPermission('admin.activity_logs.view');
  const canViewProfessionalReplies = hasPermission('admin.admin_comments.manage');

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!canViewActivityLogs && !canViewProfessionalReplies) {
        setLogs([]);
        setPagination({
          current_page: 1,
          last_page: 1,
          per_page: PAGE_SIZE,
          total: 0,
        });
        return;
      }

      const [adminResponse, replyResponse] = await Promise.all([
        canViewActivityLogs
          ? apiService.getAdminActivityLogs({
        page,
        per_page: PAGE_SIZE,
        message: messageFilter || undefined,
          admin_only: true,
          notification_category: categoryFilter || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
            })
          : Promise.resolve({
              data: [],
              pagination: { current_page: 1, last_page: 1, per_page: PAGE_SIZE, total: 0 },
              unread_count: 0,
              last_seen: null,
        }),
        canViewProfessionalReplies
          ? apiService.getProfessionalReplies({
          page: 1,
          per_page: PAGE_SIZE,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
            })
          : Promise.resolve({
              data: [],
              pagination: { current_page: 1, last_page: 1, per_page: PAGE_SIZE, total: 0 },
              last_seen: null,
        }),
      ]);

      const combined: NotificationEntry[] = [
        ...adminResponse.data.map((entry) => ({ ...entry, type: 'admin' as const })),
        ...replyResponse.data.map((entry) => ({
          ...entry,
          type: 'reply' as const,
          source: entry.source || 'professional_reply',
          created_at: entry.replied_at || entry.created_at,
          message: entry.reply,
        })),
      ].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      const entriesWithDisplay = combined.map((entry) => ({
        ...entry,
        professional_display: entry.professional_display || resolveProfessionalDisplay(entry),
      }));

      const normalizedMessageFilter = messageFilter.trim().toLowerCase();
      const filtered = entriesWithDisplay.filter((entry) => {
        if (categoryFilter === 'professional_reply' && entry.type !== 'reply') {
          return false;
        }
        if (categoryFilter && categoryFilter !== 'professional_reply' && entry.type === 'reply') {
          return false;
        }
        if (normalizedMessageFilter && !entry.message.toLowerCase().includes(normalizedMessageFilter)) {
          return false;
        }
        if (dateFrom || dateTo) {
          const entryDate = entry.created_at ? entry.created_at.slice(0, 10) : '';
          if (dateFrom && entryDate < dateFrom) {
            return false;
          }
          if (dateTo && entryDate > dateTo) {
            return false;
          }
        }
        return true;
      });

      setLogs(filtered);
      setPagination(adminResponse.pagination);
    } catch (err: any) {
      console.error('Failed to load professional replies:', err);
      setError(err.response?.data?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [page, messageFilter, categoryFilter, dateFrom, dateTo, canViewActivityLogs, canViewProfessionalReplies]);

  const handleMarkRead = useCallback(async () => {
    setMarkingRead(true);
    try {
      if (!canViewActivityLogs) {
        return;
      }
      await apiService.markNotificationsRead();
      await fetchNotifications();
      window.dispatchEvent(new CustomEvent('adminNotificationsMarkedRead'));
    } catch (error: any) {
      console.error('Failed to mark notifications read:', error);
      setError(error.response?.data?.message || 'Failed to mark notifications as read');
    } finally {
      setMarkingRead(false);
    }
  }, [fetchNotifications, canViewActivityLogs]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!loading && logs.some((entry) => entry.type === 'reply' && entry.is_new)) {
      const timer = setTimeout(() => {
        void handleMarkRead();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [logs, loading, handleMarkRead]);

  const isEmpty = !loading && logs.length === 0 && !error;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Admin Approval Queue</Typography>
        <Button
          variant="contained"
          onClick={handleMarkRead}
          disabled={markingRead || logs.length === 0}
        >
          {markingRead ? 'Marking...' : 'Mark all read'}
        </Button>
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          sx={{ gap: 2, alignItems: 'center' }}
        >
        <TextField
          label="Message contains"
          size="small"
          value={messageFilter}
          onChange={(event) => {
            setMessageFilter(event.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 200 }}
        />
        <TextField
          select
          label="Category"
          size="small"
          value={categoryFilter}
          onChange={(event) => {
            setCategoryFilter(event.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="registration">Registration</MenuItem>
          <MenuItem value="document">Document</MenuItem>
          <MenuItem value="professional_service">Professional Service</MenuItem>
          <MenuItem value="profile_approval">Profile Approval</MenuItem>
          <MenuItem value="profile_field_update">Profile Field Update</MenuItem>
          <MenuItem value="system">System</MenuItem>
          <MenuItem value="professional_reply">Comment Replies</MenuItem>
        </TextField>
        <Button
          variant="outlined"
          onClick={() => setPage(1)}
              sx={{ minWidth: 140 }}
           >
          Apply filters
        </Button>
        </Stack>
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 1 }}>
        <TextField
          type="date"
          label="From"
          size="small"
          value={dateFrom}
          onChange={(event) => {
            setDateFrom(event.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ maxWidth: 180 }}
        />
        <TextField
          type="date"
          label="To"
          size="small"
          value={dateTo}
          onChange={(event) => {
            setDateTo(event.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ maxWidth: 180 }}
        />
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : isEmpty ? (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary">
              No pending approval notifications at the moment.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Stack spacing={2}>
            {logs.map((entry) => (
              <Card key={entry.id} variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="subtitle1">{entry.message}</Typography>
                    <Chip
                      label={entry.severity}
                      color={
                        entry.severity === 'success'
                          ? 'success'
                          : entry.severity === 'warning'
                          ? 'warning'
                          : entry.severity === 'error'
                          ? 'error'
                          : 'info'
                      }
                      size="small"
                    />
                  </Box>
                  {entry.professional_display && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Professional: {entry.professional_display}
                    </Typography>
                  )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Source: {entry.source || 'System'} · {new Date(entry.created_at).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </Typography>
                  {entry.type === 'admin' && entry.metadata && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Category: {entry.metadata.notification_category || '—'} · Requested by:{' '}
                      {(() => {
                        const displayName =
                          entry.metadata.requested_by_display ||
                          entry.metadata.requested_by_name ||
                          entry.metadata.requested_by_email ||
                          '—';
                        const code = entry.metadata.requested_by_user_code;
                        return code ? `${displayName} (${code})` : displayName;
                      })()}
                    </Typography>
                  )}
                  {entry.type === 'reply' && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Comment reply from professional · {entry.comment}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Stack>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              count={pagination.last_page || 1}
              page={page}
              onChange={(_event, value) => setPage(value)}
              color="primary"
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default NotificationsPage;
